<template>
  <div>
    <!-- Tab Navigation -->
    <div class="tabs">
      <div
        v-for="tab in availableTabs"
        :key="tab.id"
        class="tab"
        :class="{ active: store.activeTab === tab.id }"
        @click="store.setActiveTab(tab.id)"
      >
        <span class="tab-icon">{{ tab.icon }}</span>
        <span class="tab-name">{{ tab.name }}</span>
      </div>
      <div class="tab-spacer"></div>
    </div>

    <!-- Tab Content -->
    <PipelineTab />

    <TeamTab />

    <div class="tab-content" v-show="store.activeTab === 'dataUpgrades'">
      <p class="description">Purchase upgrades to unlock new infrastructure, improve efficiency, and scale operations</p>
      <UpgradesTab :upgrades="store.availableDataUpgrades" />
    </div>

    <div class="tab-content" v-show="store.activeTab === 'mlUpgrades'">
      <p class="description">Advanced ML and AI upgrades to build towards AGI</p>
      <UpgradesTab :upgrades="store.availableMLUpgrades" />
    </div>

    <div class="tab-content" v-show="store.activeTab === 'achievements'">
      <p class="description">Track your progress and unlock achievements as you build your data empire</p>
      <div class="achievements">
        <div v-for="achievementId in store.state.achievements" :key="achievementId" class="achievement">
          🏆 {{ store.entityManager.getAchievementById(achievementId)?.name }}
        </div>
        <div v-if="store.state.achievements.length === 0" class="no-achievements">
          No achievements unlocked yet. Keep playing to unlock them!
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useGameStore } from '@/stores/gameStore';
import PipelineTab from './PipelineTab.vue';
import UpgradesTab from '@/components/UpgradesTab.vue';
import TeamTab from '@/components/TeamTab.vue';
import { GAME_CONFIG } from '@/config/gameConfig';
import type { GameState } from '@/types';

const store = useGameStore();

const availableTabs = computed(() => {
  return getAvailableTabs(store.state);
});

function getAvailableTabs(gameState: GameState) {
  let availableTabs = GAME_CONFIG.TABS;

  if (!gameState.upgrades.hr) {
    availableTabs = availableTabs.filter(tab => tab.id !== 'team');
  }

  if (!gameState.upgrades.ml) {
    availableTabs = availableTabs.filter(tab => tab.id !== 'mlUpgrades');
  }

  return availableTabs;
}
</script>

<style scoped>
.tabs {
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid #444;
  align-items: center;
}

.tab {
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #444;
  border-bottom: none;
  cursor: pointer;
  margin-right: 5px;
  border-radius: 5px 5px 0 0;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tab:hover {
  background: rgba(255, 255, 255, 0.1);
}

.tab.active {
  background: rgba(0, 255, 136, 0.2);
  border-color: #00ff88;
}

.tab-icon {
  font-size: 1.2em;
}

.tab-name {
  font-weight: bold;
}

.tab-spacer {
  flex: 1;
}

.tab-content {
  margin-bottom: 20px;
}

.description {
  color: #ccc;
  margin-bottom: 20px;
  font-style: italic;
  padding: 0 20px;
}

.achievements {
  padding: 0 20px;
}

.achievement {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #444;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 8px;
  color: #00ff88;
}

.no-achievements {
  color: #ccc;
  font-style: italic;
  text-align: center;
  padding: 20px;
}
</style>